"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { useRouter } from "next/navigation"
import Image from "next/image"
import { Search, X } from "lucide-react"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { getProductUrl } from "@lib/util/product"
import { convertToLocale } from "@lib/util/money"

const TOP_SUGGESTIONS = [
  "Real Madrid",
  "Liverpool",
  "Barcelona",
  "Argentina",
  "Manchester United",
  "Manchester City",
  "Arsenal",
  "Lamine Yamal",
  "Erling Haaland",
  "Bukayo Saka",
  "Jude Bellingham",
  "Mohamed Salah",
  "Lionel Messi",
]

const SORT_FILTERS = [
  { id: "bestsellers", label: "Bestsellers" },
  { id: "new", label: "New Arrivals" },
  { id: "price-asc", label: "Price: Low to High" },
  { id: "price-high", label: "Price: High to Low" },
]

type SearchProduct = {
  id: string
  title: string
  handle: string
  thumbnail: string | null
  variants: Array<{
    calculated_price?: {
      calculated_amount: number
      currency_code: string
    }
  }>
}

export default function SearchOverlay({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState("")
  const [activeSort, setActiveSort] = useState("bestsellers")
  const [products, setProducts] = useState<SearchProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [visible, setVisible] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const router = useRouter()
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Animate in on mount, lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden"
    // Trigger slide-down on next frame
    requestAnimationFrame(() => setVisible(true))
    inputRef.current?.focus()
    return () => {
      document.body.style.overflow = ""
    }
  }, [])

  // Close with animation
  const handleClose = useCallback(() => {
    setVisible(false)
    setTimeout(() => onClose(), 300)
  }, [onClose])

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose()
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [handleClose])

  // Fetch products
  const fetchProducts = useCallback(
    async (searchQuery: string) => {
      setLoading(true)
      try {
        const params = new URLSearchParams({
          limit: "8",
          region_id: "reg_01KNCTBEME5V06RTEHXD8ZDTAZ",
          fields: "*variants.calculated_price",
        })
        if (searchQuery.trim()) {
          params.set("q", searchQuery.trim())
        }

        const res = await fetch(`/api/search?${params.toString()}`)
        if (res.ok) {
          const data = await res.json()
          setProducts(data.products || [])
        }
      } catch {
        // Fallback: direct Medusa API call via backend
        try {
          const params = new URLSearchParams({
            limit: "8",
            region_id: "reg_01KNCTBEME5V06RTEHXD8ZDTAZ",
            fields: "*variants.calculated_price",
          })
          if (searchQuery.trim()) {
            params.set("q", searchQuery.trim())
          }
          const backendUrl = process.env.NEXT_PUBLIC_MEDUSA_BACKEND_URL || "https://ukss-medusa-admin.veldev.com"
          const pk = process.env.NEXT_PUBLIC_MEDUSA_PUBLISHABLE_KEY || ""
          const res = await fetch(`${backendUrl}/store/products?${params.toString()}`, {
            headers: { "x-publishable-api-key": pk },
          })
          if (res.ok) {
            const data = await res.json()
            setProducts(data.products || [])
          }
        } catch {
          setProducts([])
        }
      } finally {
        setLoading(false)
      }
    },
    []
  )

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchProducts(query)
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, fetchProducts])

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = query.trim()
    if (!trimmed) return
    onClose()
    router.push(`/results/${encodeURIComponent(trimmed)}`)
  }

  return (
    <div className={`fixed inset-0 z-overlay bg-white overflow-y-auto transition-transform duration-300 ease-out ${
      visible ? "translate-y-0" : "-translate-y-full"
    }`}>
      {/* Header */}
      <div className="border-b border-gray-200">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Image
            src="/images/uksoccershop-logo.png"
            alt="UK Soccer Shop"
            width={180}
            height={36}
            className="h-auto"
          />
          <button
            onClick={handleClose}
            className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-900 transition-colors"
            aria-label="Close search"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 py-6">
        <div className="flex gap-8 lg:gap-12">
          {/* Left sidebar — Top Suggestions */}
          <div className="hidden md:block w-48 lg:w-56 flex-shrink-0">
            <h3 className="text-sm font-bold text-gray-900 mb-4">
              Top Suggestions
            </h3>
            <ul className="space-y-2.5">
              {TOP_SUGGESTIONS.map((suggestion) => (
                <li key={suggestion}>
                  <button
                    onClick={() => handleSuggestionClick(suggestion)}
                    className={`text-sm transition-colors text-left w-full ${
                      query === suggestion
                        ? "text-brand-primary font-semibold"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {suggestion}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Right content — Search + Products */}
          <div className="flex-1 min-w-0">
            {/* Search input */}
            <form onSubmit={handleSubmit} className="mb-5">
              <div className="flex items-center border border-gray-300 rounded-full px-4 py-2.5 focus-within:ring-2 focus-within:ring-brand-navy focus-within:border-brand-navy">
                <Search className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
                <input
                  ref={inputRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search for club, country, player"
                  className="flex-1 bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
                />
              </div>
            </form>

            {/* Sort filter tabs */}
            <div className="flex gap-2 mb-6 overflow-x-auto scrollbar-hide">
              {SORT_FILTERS.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setActiveSort(filter.id)}
                  className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-medium border transition whitespace-nowrap ${
                    activeSort === filter.id
                      ? "bg-brand-navy text-white border-brand-navy"
                      : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {/* Products grid */}
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-square bg-gray-100 rounded-lg mb-2" />
                    <div className="h-3 bg-gray-100 rounded w-3/4 mb-1" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {products
                  .filter((p) => {
                    const price = p.variants?.[0]?.calculated_price?.calculated_amount
                    return price != null && price > 0
                  })
                  .map((product) => {
                    const variant = product.variants?.[0]
                    const price = variant?.calculated_price?.calculated_amount != null
                      ? convertToLocale({ amount: variant.calculated_price.calculated_amount, currency_code: variant.calculated_price.currency_code })
                      : null
                    return (
                  <LocalizedClientLink
                    key={product.id}
                    href={getProductUrl(product.handle)}
                    onClick={handleClose}
                    className="group"
                  >
                    <div className="aspect-square relative mb-2 bg-gray-50 rounded-lg overflow-hidden">
                      {product.thumbnail ? (
                        <Image
                          src={product.thumbnail}
                          alt={product.title}
                          fill
                          className="object-contain p-2 group-hover:scale-105 transition-transform duration-300"
                          sizes="(max-width: 768px) 50vw, 25vw"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-gray-300">
                          No image
                        </div>
                      )}
                    </div>
                    <h4 className="text-xs font-medium text-gray-900 line-clamp-2 mb-1">
                      {product.title}
                    </h4>
                    {price && (
                      <p className="text-sm font-bold text-gray-900">
                        {price}
                      </p>
                    )}
                  </LocalizedClientLink>
                    )
                  })}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg font-medium mb-1">No results found</p>
                <p className="text-sm">Try a different search term</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
